import React, { useEffect, useState } from "react";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { createAndGenerateProposal, getUserProposalIds } from "../services/proposals";
import { analytics, auth } from "../firebase";
import { logEvent } from "firebase/analytics";
import { generateHeadline } from "../utils/headline";
import { useMutationAuthGate } from "../hooks/useMutationAuthGate";

export default function Landing() {
  const defaultHeadline = "Turn your app idea into a build-ready plan.";
  const sampleIdeas = {
    finance:
      "Build a personal finance mobile app for young professionals that links bank accounts, auto-categorizes spending, and provides weekly cash-flow alerts with simple savings goals.",
    healthcare:
      "Build a healthcare coordination app for patients with chronic conditions that tracks medications, sends care reminders, and shares symptom updates with caregivers and clinicians.",
  } as const;
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showPresetIdeaModal, setShowPresetIdeaModal] = useState(false);
  const [headline, setHeadline] = useState(defaultHeadline);
  const [userProposalIds, setUserProposalIds] = useState<string[]>([]);
  const [user, setUser] = useState<any>(null);
  const { requireSignedInStatus, authModal } = useMutationAuthGate();
  const navigate = useNavigate();

  useEffect(() => {
    const kw = new URLSearchParams(window.location.search).get("kw") ?? undefined;
    if (!kw) return;
    setHeadline(generateHeadline(kw));
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u && !u.isAnonymous) {
        const ids = await getUserProposalIds(u.uid);
        setUserProposalIds(ids);
      } else {
        setUserProposalIds([]);
      }
    });
    return () => unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!idea.trim()) {
      setShowPresetIdeaModal(true);
      return;
    }

    if (analytics) {
      logEvent(analytics, "generate_proposal_click", { idea_length: idea.length });
    }

    try {
      setLoading(true);
      const proposalId = await createAndGenerateProposal(idea.trim());
      navigate(`/p/${proposalId}`);
    } catch (e: any) {
      console.error(e);
      setErr(e.message ?? "Something went wrong.");
      setLoading(false);
    }
  }

  function focusIdeaInput() {
    const ideaInput = document.getElementById("idea") as HTMLTextAreaElement | null;
    if (!ideaInput) return;
    ideaInput.scrollIntoView({ behavior: "smooth", block: "center" });
    ideaInput.focus();
  }

  function applyPresetIdea(preset: keyof typeof sampleIdeas) {
    setIdea(sampleIdeas[preset]);
    setShowPresetIdeaModal(false);
    setErr(null);
    window.setTimeout(() => {
      focusIdeaInput();
    }, 0);
  }

  return (
    <div className="container landing-page">
      <header className="hero">
        <span className="hero-badge">App Robot for founders</span>
        <h1>{headline}</h1>
        <p className="hero-subheadline">
          App Robot helps you turn a vague app idea into a structured, build-ready plan with clearer MVP scope,
          timeline, and next steps.
        </p>
      </header>

      <section className="trust-strip" aria-label="What you get">
        <div className="trust-item">Clear MVP scope</div>
        <div className="trust-item">Timeline by phase</div>
        <div className="trust-item">Practical next steps</div>
      </section>

      <form className="card idea-form-card" onSubmit={handleSubmit}>
        <div className="form-header">
          <h2>Start with your app concept</h2>
          <p>Describe who it helps, what problem it solves, and your first milestone.</p>
        </div>
        <label htmlFor="idea">Describe your app idea</label>
        <textarea
          id="idea"
          className="idea-input"
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          placeholder="Describe your mobile app idea: who it's for, the main problem it solves, and the core features for your first version."
          rows={7}
        />
        <button type="submit" className="primary-cta" disabled={loading}>
          {loading ? "Building your plan…" : "Build My Plan"}
        </button>
        <small className="muted">No sign-up required. We create an anonymous session so you can start immediately.</small>
        {err && <div className="error">{err}</div>}
      </form>

      <section className="card history-card">
        <h2>History proposals</h2>
        {user && !user.isAnonymous ? (
          userProposalIds.length ? (
            <ul className="history-list">
              {[...userProposalIds].reverse().map((id) => (
                <li key={id}>
                  <Link to={`/p/${id}`}>{id}</Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No generated proposals yet. Your new plans will appear here.</p>
          )
        ) : (
          <>
            <p className="muted">Sign in to access your history proposals.</p>
            <button
              type="button"
              className="secondary-cta history-login-btn"
              onClick={() => requireSignedInStatus("Sign in to access your history proposals.")}
            >
              Log in to access history proposals
            </button>
          </>
        )}
      </section>

      <section className="landing-section card">
        <h2>How it works</h2>
        <ol className="steps-list">
          <li>
            <span className="step-number">1</span>
            <div>
              <h3>Describe your vision</h3>
              <p>Share your idea in plain language, including users, workflow, and business goals.</p>
            </div>
          </li>
          <li>
            <span className="step-number">2</span>
            <div>
              <h3>App Robot structures your plan</h3>
              <p>Get a clean MVP scope, key features, phased timeline, and implementation direction.</p>
            </div>
          </li>
          <li>
            <span className="step-number">3</span>
            <div>
              <h3>Move forward with confidence</h3>
              <p>Use your plan to align co-founders, brief developers, or prepare for investor conversations.</p>
            </div>
          </li>
        </ol>
      </section>

      <section className="landing-section pain-outcome-grid">
        <article className="card">
          <h2>Founder pain when ideas stay vague</h2>
          <ul>
            <li>Scope keeps changing, so timelines and costs stay uncertain.</li>
            <li>Technical conversations become confusing and hard to evaluate.</li>
            <li>Momentum drops because next steps are unclear.</li>
          </ul>
        </article>
        <article className="card">
          <h2>Founder outcome with a build-ready plan</h2>
          <ul>
            <li>You know exactly what belongs in MVP vs later phases.</li>
            <li>You can discuss trade-offs and delivery timelines with clarity.</li>
            <li>You have concrete next steps to move from idea to execution.</li>
          </ul>
        </article>
      </section>

      <section className="card final-cta-card">
        <h2>Ready to turn your idea into a concrete plan?</h2>
        <p>Create a plan you can confidently share with co-founders, developers, and investors.</p>
        <button
          type="button"
          className="secondary-cta"
          onClick={() => {
            if (analytics) logEvent(analytics, "landing_final_cta_click");
            focusIdeaInput();
          }}
        >
          Create My Build Plan
        </button>
      </section>

      <Footer />
      {authModal}

      {showPresetIdeaModal && (
        <div className="proposal-modal-overlay">
          <div className="proposal-modal landing-preset-modal">
            <h2>Need help getting started?</h2>
            <p className="landing-preset-modal-copy">
              Your idea box is empty. Would you like to start with a preset app idea?
            </p>
            <div className="landing-preset-options">
              <button type="button" className="landing-preset-option-btn" onClick={() => applyPresetIdea("finance")}>
                Sample Finance App Idea
              </button>
              <button
                type="button"
                className="landing-preset-option-btn"
                onClick={() => applyPresetIdea("healthcare")}
              >
                Sample Healthcare App Idea
              </button>
            </div>
            <div className="proposal-modal-actions">
              <button
                type="button"
                className="proposal-secondary-btn"
                onClick={() => setShowPresetIdeaModal(false)}
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
